import React, { useState } from "react";
import { ChevronDown, ChevronUp, Check, X, ShieldCheck } from "lucide-react";
import { Staff } from "../Table/StaffTable";
import { useGetRolesQuery } from "../../redux/services/staffApi";
import {
  useGetPermissionCategoriesQuery,
  useGetPermissionsByRoleQuery,
} from "../../redux/services/settingsRoleApi";

const toTitleCase = (str: string) =>
  str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

type PermissionsContentProps =
  | { mode: "staff"; staff: Staff; permissions?: any[]; loading?: boolean }
  | { mode: "config"; permissions?: any[]; loading?: boolean };

const PermissionsContent: React.FC<PermissionsContentProps> = (props) => {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const { data: rolesData } = useGetRolesQuery();

  const roleId =
    props.mode === "staff"
      ? props.staff.role_id?.toString() ||
        rolesData?.find((r: any) => r.name === props.staff.role)?.id?.toString()
      : undefined;

  const { data: rolePermissionsRaw, isLoading: rolePermissionsLoading } =
    useGetPermissionsByRoleQuery(roleId ?? "", {
      skip: props.mode !== "staff" || !roleId,
    });

  const { data: categoriesRaw, isLoading: categoriesLoading } =
    useGetPermissionCategoriesQuery();

  // ── Extract assigned permissions ──────────────────────
  const assignedPermissions: { id: string; name: string; description?: string }[] = (() => {
    if (!rolePermissionsRaw) return [];
    const p =
      rolePermissionsRaw?.data?.data?.permissions ??
      rolePermissionsRaw?.data?.permissions ??
      rolePermissionsRaw?.permissions ??
      (Array.isArray(rolePermissionsRaw?.data?.data) ? rolePermissionsRaw.data.data : null) ??
      (Array.isArray(rolePermissionsRaw?.data) ? rolePermissionsRaw.data : null) ??
      (Array.isArray(rolePermissionsRaw) ? rolePermissionsRaw : null) ??
      [];
    return Array.isArray(p) ? p : [];
  })();

  const assignedNames = new Set(
    assignedPermissions.map((p) => (p.name ?? "").toLowerCase().trim())
  );

  // ── Extract categories ────────────────────────────────
  const allCategories: any[] = (() => {
    if (!categoriesRaw) return [];
    const c =
      categoriesRaw?.data?.data?.categories ??
      categoriesRaw?.data?.categories ??
      categoriesRaw?.categories ??
      (Array.isArray(categoriesRaw?.data?.data) ? categoriesRaw.data.data : null) ??
      (Array.isArray(categoriesRaw?.data) ? categoriesRaw.data : null) ??
      (Array.isArray(categoriesRaw) ? categoriesRaw : null) ??
      [];
    return Array.isArray(c) ? c : [];
  })();

  // ── Fallback: group assigned permissions by last word ─
  const fallbackCategories: any[] = (() => {
    if (assignedPermissions.length === 0) return [];
    const groups: Record<string, any[]> = {};
    assignedPermissions.forEach((p) => {
      const parts = p.name?.split(" ") ?? [];
      const module = toTitleCase(parts[parts.length - 1] ?? "General");
      if (!groups[module]) groups[module] = [];
      groups[module].push(p);
    });
    return Object.entries(groups).map(([name, permissions]) => ({
      id: name,
      name,
      permissions,
    }));
  })();

  const categories = allCategories.length > 0 ? allCategories : fallbackCategories;

  // ── Loading ───────────────────────────────────────────
  if (categoriesLoading || rolePermissionsLoading) {
    return (
      <div className="flex items-center justify-center py-12 gap-3">
        <div className="h-5 w-5 border-2 border-gray-200 border-t-[#143887] rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Loading permissions...</span>
      </div>
    );
  }

  // ── No data ───────────────────────────────────────────
  if (categories.length === 0 && assignedPermissions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-2">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
          <ShieldCheck size={18} className="text-gray-300" />
        </div>
        <p className="text-sm text-gray-500">No permissions assigned to this role.</p>
        {!roleId && (
          <p className="text-xs text-red-400">Role ID could not be resolved.</p>
        )}
      </div>
    );
  }

  const totalAssigned = assignedPermissions.length;
  const roleName =
    props.mode === "staff"
      ? (props.staff.role ?? "")
      : "";

  // ── Render ────────────────────────────────────────────
  return (
    <div className="w-full">

      {/* Summary header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-sm bg-[#e4ebf8]">
            <ShieldCheck size={15} className="text-[#143887]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#00032A]">
              {props.mode === "staff" ? "Assigned Permissions" : "Available Permissions"}
            </p>
            {roleName && (
              <p className="text-xs text-gray-400">Role: {roleName}</p>
            )}
          </div>
        </div>
        {totalAssigned > 0 && (
          <span className="text-xs font-medium text-[#143887] bg-[#e4ebf8] border border-[#c5d5ef] px-2.5 py-1 rounded-full">
            {totalAssigned} permission{totalAssigned !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Accordion list */}
      <div className="rounded-xl border border-gray-100 overflow-hidden">
        {categories.map((section: any, idx: number) => {
          const isOpen = openSection === section.name;
          const sectionPerms: any[] = section.permissions ?? [];
          const assignedCount = sectionPerms.filter((p) =>
            assignedNames.has((p.name ?? "").toLowerCase().trim())
          ).length;
          const allAssigned = assignedCount === sectionPerms.length && sectionPerms.length > 0;
          const noneAssigned = assignedCount === 0;

          return (
            <div
              key={section.id ?? section.name}
              className={idx > 0 ? "border-t border-gray-100" : ""}
            >
              {/* Section header */}
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? null : section.name)}
                className="flex w-full items-center justify-between px-4 py-3.5 hover:bg-gray-50/80 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* Color dot */}
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      allAssigned
                        ? "bg-green-500"
                        : noneAssigned
                        ? "bg-gray-200"
                        : "bg-amber-400"
                    }`}
                  />
                  <span className="text-sm font-medium text-[#00032A]">
                    {toTitleCase(section.name)}
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  {/* Count badge */}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      allAssigned
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : noneAssigned
                        ? "bg-gray-50 text-gray-400 border border-gray-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                  >
                    {assignedCount}/{sectionPerms.length}
                  </span>
                  {isOpen
                    ? <ChevronUp size={15} className="text-gray-400 flex-shrink-0" />
                    : <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
                  }
                </div>
              </button>

              {/* Permissions list */}
              {isOpen && (
                <div className="bg-gray-50/50 border-t border-gray-100">
                  {sectionPerms.length === 0 ? (
                    <p className="px-5 py-3 text-xs text-gray-400 italic">
                      No permissions in this category.
                    </p>
                  ) : (
                    sectionPerms.map((perm: any, permIdx: number) => {
                      const has = assignedNames.has((perm.name ?? "").toLowerCase().trim());
                      return (
                        <div
                          key={perm.id ?? perm.name}
                          className={`flex items-center justify-between px-5 py-3 transition-colors hover:bg-white ${
                            permIdx > 0 ? "border-t border-gray-100" : ""
                          }`}
                        >
                          <div className="flex-1 min-w-0 pr-4">
                            <p className="text-sm text-[#00032A]">
                              {toTitleCase(perm.name)}
                            </p>
                            {perm.description && perm.description !== perm.name && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {perm.description}
                              </p>
                            )}
                          </div>
                          {has ? (
                            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-green-50 border border-green-200 flex-shrink-0">
                              <Check size={13} className="text-green-600" strokeWidth={2.5} />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 border border-gray-200 flex-shrink-0">
                              <X size={13} className="text-gray-300" strokeWidth={2.5} />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Flat fallback if categories failed but permissions exist */}
      {allCategories.length === 0 && assignedPermissions.length > 0 && fallbackCategories.length === 0 && (
        <div className="rounded-xl border border-gray-100 overflow-hidden mt-2">
          {assignedPermissions.map((perm, idx) => (
            <div
              key={perm.id ?? perm.name}
              className={`flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors ${
                idx > 0 ? "border-t border-gray-100" : ""
              }`}
            >
              <p className="text-sm text-[#00032A]">{toTitleCase(perm.name)}</p>
              <div className="flex items-center justify-center w-6 h-6 rounded-md bg-green-50 border border-green-200">
                <Check size={13} className="text-green-600" strokeWidth={2.5} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PermissionsContent;